import { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Empty, Tooltip, Typography } from "antd";
import { LuArrowUp, LuArrowDown } from "react-icons/lu";
import type { ProcessedWidgetData } from "../../Processing/types";
import type { PieSortOption } from "../../Processing/types";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";

const { Text, Title } = Typography;

const PIE_SORT_KINDS = [
  {
    kind: "value",
    label: "Value",
    ascValue: "value-asc" as PieSortOption,
    descValue: "value-desc" as PieSortOption,
    ascTip: "Sort slices by value, smallest first",
    descTip: "Sort slices by value, largest first",
  },
  {
    kind: "name",
    label: "Name",
    ascValue: "alpha-asc" as PieSortOption,
    descValue: "alpha-desc" as PieSortOption,
    ascTip: "Sort slices alphabetically (A → Z)",
    descTip: "Sort slices alphabetically (Z → A)",
  },
];

interface PiePreviewPanelProps {
  previewData: ProcessedWidgetData | null;
  title: string;
  hasMetric: boolean;
  showLegend: boolean;
  summary: string;
  configuredCount: number;
  totalSlices: number;
  sortBy: PieSortOption | null;
  onSortChange: (sortBy: PieSortOption | null) => void;
}

export function PiePreviewPanel({ previewData, title, hasMetric, showLegend, summary, configuredCount, totalSlices, sortBy, onSortChange }: PiePreviewPanelProps) {
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);
  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const [chartSize, setChartSize] = useState<{ width: number; height: number } | undefined>();

  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setChartSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const chartOptions =
    previewData?.type === "pie_chart"
      ? {
          ...previewData.chart.chartOptions,
          chart: {
            ...((previewData.chart.chartOptions.chart as Record<string, unknown>) ?? {}),
            width: chartSize?.width ?? null,
            height: chartSize?.height ?? null,
            marginTop: showLegend ? 40 : 0,
            reflow: true,
            animation: false,
          },
          title: { text: "" },
          legend: {
            ...((previewData.chart.chartOptions.legend as Record<string, unknown>) ?? {}),
            enabled: showLegend,
            align: "center" as const,
            verticalAlign: "top" as const,
            layout: "horizontal" as const,
          },
          exporting: { enabled: false },
        }
      : null;

  return (
    <>
      <div className="awm-preview-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
          <Title level={5} style={{ margin: 0, flexShrink: 0 }}>
            {title || "Pie Chart Preview"}
          </Title>
          {hasMetric && <PreviewDisclaimer />}
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {summary}
        </Text>

        <div className="awm-preview-chart-area" ref={chartAreaRef}>
          {hasMetric && chartOptions ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              ref={chartRef}
              immutable={false}
              containerProps={{ style: { width: "100%", height: "100%", minHeight: 0 } }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Pick a metric to see a preview"
            />
          )}
        </div>
      </div>

      <div className="awm-groupby-bar">
        <div className="awm-sort-row">
          <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
            Sort by
          </Text>
          <div className="awm-groupby-options">
            {PIE_SORT_KINDS.map((kind) => {
              const isAsc = sortBy === kind.ascValue;
              const isDesc = sortBy === kind.descValue;
              const isActive = isAsc || isDesc;
              const tooltip = isAsc ? kind.ascTip : isDesc ? kind.descTip : "";

              function handleClick() {
                if (sortBy === kind.ascValue) {
                  onSortChange(kind.descValue);
                } else if (sortBy === kind.descValue) {
                  onSortChange(null);
                } else {
                  onSortChange(kind.ascValue);
                }
              }

              return (
                <Tooltip key={kind.kind} title={tooltip}>
                  <button
                    className={`awm-groupby-chip${isActive ? " active" : ""}`}
                    onClick={handleClick}
                  >
                    {kind.label}
                    {isAsc && <LuArrowUp style={{ marginLeft: 4, fontSize: 11, verticalAlign: "middle" }} />}
                    {isDesc && <LuArrowDown style={{ marginLeft: 4, fontSize: 11, verticalAlign: "middle" }} />}
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </div>

      <Text
        type="secondary"
        style={{ display: "block", textAlign: "center", marginTop: 6, fontSize: 12 }}
      >
        {!hasMetric
          ? "Pick a metric to see a preview..."
          : `${configuredCount} of ${totalSlices} slice${totalSlices !== 1 ? "s" : ""} configured`}
      </Text>
    </>
  );
}
